import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import {
  ContestPositionArrowIcon,
  RegistrationsIcon,
  ViewsIcon,
} from "@/assets/icons";
import Button from "@/ui/Button/Button";

import cls from "./styles.module.scss";

export type ContestRatingType = "views" | "registrations";

interface BloggerContestLeaderboardProps {
  participants: BloggerContestParticipant[];
  me: BloggerContestParticipant | null;
  ratingType: ContestRatingType;
}

export interface BloggerContestParticipant {
  participantId: number;
  displayName: string;
  place: number;
  score: number;
  secondaryScore: number | null;
}

const podiumClasses = [cls.place1, cls.place2, cls.place3];
const formatNumber = (value: number) => value.toLocaleString("ru-RU");

export const BloggerContestLeaderboard = ({
  participants,
  me,
  ratingType,
}: BloggerContestLeaderboardProps) => {
  const meRowRef = useRef<HTMLLIElement | null>(null);
  const isMeListed = participants.some(
    (participant) => participant.participantId === me?.participantId
  );
  const [isMeVisible, setIsMeVisible] = useState(isMeListed);
  const [meDirection, setMeDirection] = useState<"up" | "down">("down");

  useEffect(() => {
    if (!isMeListed || !meRowRef.current) {
      setIsMeVisible(false);
      setMeDirection("down");
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsMeVisible(entry.isIntersecting);

        if (!entry.isIntersecting) {
          const viewportTop = entry.rootBounds?.top ?? 0;
          const viewportBottom = entry.rootBounds?.bottom ?? window.innerHeight;
          const viewportCenter = (viewportTop + viewportBottom) / 2;
          const rowCenter =
            (entry.boundingClientRect.top + entry.boundingClientRect.bottom) /
            2;

          setMeDirection(rowCenter < viewportCenter ? "up" : "down");
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(meRowRef.current);
    return () => observer.disconnect();
  }, [isMeListed, ratingType, me?.participantId]);

  return (
    <div className={cls.wrapper}>
      <ol className={cls.list}>
        {participants.map((participant) => {
          const isMe = me?.participantId === participant.participantId;

          return (
            <li
              key={participant.participantId}
              ref={isMe ? meRowRef : undefined}
              className={isMe ? cls.meRow : undefined}
            >
              <span
                className={classNames(
                  cls.place,
                  podiumClasses[participant.place - 1]
                )}
              >
                {participant.place}
              </span>
              <span className={cls.person}>
                <strong>{isMe ? "Ты" : participant.displayName}</strong>
                <small>
                  {ratingType === "views" ? (
                    <RegistrationsIcon />
                  ) : (
                    <ViewsIcon />
                  )}
                  <span>
                    {participant.secondaryScore === null
                      ? "—"
                      : formatNumber(participant.secondaryScore)}
                  </span>
                </small>
              </span>
              <strong className={cls.score}>
                {formatNumber(participant.score)}
              </strong>
            </li>
          );
        })}
      </ol>

      <AnimatePresence initial={false}>
        {me && !isMeVisible && (
          <motion.div
            className={cls.myPlace}
            initial={{ opacity: 0, y: 18, scale: 0.88, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: 12, scale: 0.9, filter: "blur(4px)" }}
            transition={{
              type: "spring",
              stiffness: 460,
              damping: 32,
              mass: 0.72,
            }}
          >
            <span>Ты · {me.place} место</span>
            <Button
              trigger="ref.contest.my_position"
              variant="iconOnly"
              className={classNames(
                cls.positionButton,
                meDirection === "up" && cls.positionButtonUp
              )}
              icon={<ContestPositionArrowIcon />}
              ariaLabel="Показать моё место"
              onClick={() =>
                meRowRef.current?.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                })}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
